/* eslint-disable react/no-this-in-sfc */
import React, { useContext, useState } from 'react';
import { Container, Row, Col, Button } from 'react-bootstrap';
import * as Yup from 'yup';
import { Form, Formik, ErrorMessage } from 'formik';
import {
    AsyncSelectField,
    SubmitButton,
    AsyncMultiSelectField,
    MultiSelectField,
} from '@nintendo/forms';
import Tooltip from '@nintendo/forms/lib/js/components/common/Tooltip';
import moment from 'moment-timezone';
import { useAsync } from 'react-commons';
import ConfigContext from '../../../contexts/ConfigContext';
import { getAllAmUsers } from '../../../api/amUserApi';
import {
    INITIAL_SELECT_ID,
    DATE_ONLY_FORMAT,
    SERVER_TIMEZONE,
    AmStatusType,
} from '../../../utils/Constants';
import { noOptionsHandler } from '../../../utils/displayUtils';
import DateSearchField from '../../common/DateSearchField';
import { SearchObjectType } from '../../common/datatables/DataGridControlled';
import useAsyncWithState from '../../../hooks/useAsyncWithState';
import { getTemplates } from '../../../api/amTemplateApi';
import { AmTemplateType } from '../../../api/amTemplateApiTypes';
import { ProjectRoleType } from '../../../api/projectRoleApiTypes';
import { getProjectRoles } from '../../../api/projectRoleApi';

interface UserManagementFilterProps {
    setSearchObject: Function;
    searchString: string;
    searching: boolean;
    onSubmit: () => void;
}

export interface UserManagementSearchFormType {
    personId: string;
    personName: string;
    amStatuses: string[];
    dateFrom: string | undefined;
    dateTo: string | undefined;
    templateCodes: string[];
    projectRoleIds: string[];
}

const UserManagementFilter = ({
    setSearchObject,
    searchString,
    searching,
    onSubmit,
}: UserManagementFilterProps) => {
    const searchObjectInitial = JSON.parse(searchString);
    const config = useContext(ConfigContext);

    const [refresh, setRefresh] = useState(0);

    const { value: projectRoles } = useAsync<Array<ProjectRoleType>>(
        () => getProjectRoles(config),
        [] as Array<ProjectRoleType>,
    );

    const userManagementRoles = projectRoles.filter(
        (projectRole) => !projectRole.projectRoleMeaning.includes('Template Approver'),
    );

    const statusOptions = [AmStatusType.Approved, AmStatusType.Hold, AmStatusType.Pending].map(
        (key) => ({
            value: (AmStatusType as { [key: string]: string })[key],
            label: (AmStatusType as { [key: string]: string })[key],
        }),
    );

    const { value: templates } = useAsyncWithState(
        () => getTemplates(config),
        [] as AmTemplateType[],
    );

    const initialValues: UserManagementSearchFormType = {
        personId: searchObjectInitial.personId || '',
        personName: searchObjectInitial.personName || '',
        amStatuses: searchObjectInitial.amStatuses ? searchObjectInitial.amStatuses : [],
        dateFrom: searchObjectInitial.dateFrom,
        dateTo: searchObjectInitial.dateTo,
        templateCodes: searchObjectInitial.templateCodes ? searchObjectInitial.templateCodes : [],
        projectRoleIds: searchObjectInitial.projectRoleIds
            ? searchObjectInitial.projectRoleIds
            : [],
    };

    const userManagementsValidationSchema = Yup.object().shape({
        personId: Yup.number().required('Person is required'),
        dateFrom: Yup.date()
            .nullable()
            .typeError('From Date is invalid')
            .test(
                'date-from-to-check',
                'Date From must be before or equal to Date To',
                function test(dateFrom) {
                    if (!dateFrom || !this.parent.dateTo) {
                        return true;
                    }
                    const fromMoment = moment(dateFrom);
                    const toMoment = moment(this.parent.dateTo);
                    return fromMoment.isSameOrBefore(toMoment);
                },
            ),
        dateTo: Yup.date().nullable().typeError('To Date is invalid'),
    });

    return (
        <Container fluid className="border-top pt-3">
            <Formik
                initialValues={initialValues}
                validationSchema={userManagementsValidationSchema}
                onSubmit={async (values, { setSubmitting }) => {
                    const searchObject: SearchObjectType = {
                        personId: values.personId,
                        personName: values.personName,
                        dateFrom: values.dateFrom ? new Date(values.dateFrom).toUTCString() : null,
                        dateTo: values.dateTo ? new Date(values.dateTo).toUTCString() : null,
                        amStatuses: values.amStatuses.length > 0 ? values.amStatuses : null,
                        templateCodes:
                            values.templateCodes.length > 0 ? values.templateCodes : null,
                        projectRoleIds:
                            values.projectRoleIds.length > 0 ? values.projectRoleIds : null,
                        refresh,
                    };

                    setSearchObject(searchObject);
                    setRefresh(refresh + 1);
                    setSubmitting(false);
                    onSubmit();
                }}
                render={({ values, touched, errors, setFieldValue, setValues, isSubmitting }) => (
                    <Form>
                        <Container fluid>
                            <Row>
                                <Col>
                                    <Row>
                                        <Col>
                                            <AsyncSelectField
                                                label="Person"
                                                name="personId"
                                                touched={touched.personId}
                                                errors={errors.personId}
                                                testId="personId"
                                                isRequired
                                                asyncSelectProps={{
                                                    loadOptions: async (inputValue: string) => {
                                                        if (inputValue.length > 2) {
                                                            const users = await getAllAmUsers(
                                                                config,
                                                                inputValue,
                                                            );
                                                            if (users) {
                                                                return users.map((user) => ({
                                                                    value: user.personId.toString(),
                                                                    label: `${
                                                                        user.knownAs
                                                                            ? `${user.knownAs} (${user.firstName})`
                                                                            : user.firstName
                                                                    } ${user.lastName}${
                                                                        user.title
                                                                            ? ` (${user.title})`
                                                                            : ''
                                                                    } (${user.personId})`,
                                                                }));
                                                            }
                                                        }
                                                        return [];
                                                    },
                                                    value: values.personId
                                                        ? {
                                                              value: values.personId.toString(),
                                                              label: values.personName,
                                                          }
                                                        : null,
                                                    noOptionsMessage: noOptionsHandler,
                                                }}
                                                onChange={(choice) => {
                                                    if (choice) {
                                                        setFieldValue('personName', choice.label);
                                                        setFieldValue('personId', choice.value);
                                                    } else {
                                                        setFieldValue('personName', '');
                                                        setFieldValue(
                                                            'personId',
                                                            INITIAL_SELECT_ID,
                                                        );
                                                    }
                                                }}
                                                labelCssClass="col-sm-3"
                                                contentCssClass="col-sm-9"
                                            />
                                        </Col>
                                        <Col>
                                            <MultiSelectField
                                                selectProps={
                                                    {
                                                        closeMenuOnSelect: false,
                                                    } as any
                                                }
                                                selectedOptions={
                                                    values.projectRoleIds
                                                        ? userManagementRoles
                                                              .filter((userManagementRole) =>
                                                                  values.projectRoleIds.includes(
                                                                      userManagementRole.projectRoleId.toString(),
                                                                  ),
                                                              )
                                                              .map((userManagementRole) => ({
                                                                  label: userManagementRole.projectRoleMeaning,
                                                                  value: userManagementRole.projectRoleId.toString(),
                                                              }))
                                                        : []
                                                }
                                                options={userManagementRoles.map(
                                                    (userManagementRole) => ({
                                                        label: userManagementRole.projectRoleMeaning,
                                                        value: userManagementRole.projectRoleId.toString(),
                                                    }),
                                                )}
                                                label="Role"
                                                name="projectRoleIds"
                                                touched={touched.projectRoleIds}
                                                errors={errors.projectRoleIds}
                                                labelCssClass="col-sm-4"
                                                contentCssClass="col-sm-8"
                                                testId="projectRoleIds"
                                            />
                                        </Col>
                                        <Col />
                                    </Row>
                                    <Row>
                                        <Col>
                                            <div
                                                className={`form-group row ${
                                                    touched.dateFrom &&
                                                    errors.dateFrom &&
                                                    'has-error'
                                                } ${
                                                    touched.dateTo && errors.dateTo && 'has-error'
                                                }`}
                                            >
                                                <div className="col-sm-3 control-label breakdown-label">
                                                    Date &nbsp;
                                                    <Tooltip
                                                        text="Searches both creation and approval dates"
                                                        name="dateFrom"
                                                    />
                                                </div>
                                                <div className="col-sm-9">
                                                    <div
                                                        className={`form-group row mb-0 ${
                                                            touched.dateFrom &&
                                                            errors.dateFrom &&
                                                            'has-error'
                                                        } ${
                                                            touched.dateTo &&
                                                            errors.dateTo &&
                                                            'has-error'
                                                        }`}
                                                    >
                                                        <DateSearchField
                                                            label="Date From"
                                                            name="dateFrom"
                                                            flatpickrProps={{
                                                                options: {
                                                                    altInputClass: 'form-control',
                                                                    altFormat: DATE_ONLY_FORMAT,
                                                                    enableTime: false,
                                                                    dateFormat: DATE_ONLY_FORMAT,
                                                                    defaultDate: '',
                                                                },
                                                                value: values.dateFrom
                                                                    ? moment(
                                                                          values.dateFrom,
                                                                      ).format(DATE_ONLY_FORMAT)
                                                                    : undefined,
                                                            }}
                                                            timezone={SERVER_TIMEZONE}
                                                            touched={touched.dateFrom}
                                                            errors={errors.dateFrom}
                                                            contentCssClass="col-sm-6"
                                                            placeholder="from"
                                                            testId="dateFrom"
                                                        />
                                                        <DateSearchField
                                                            label="Date To"
                                                            name="dateTo"
                                                            flatpickrProps={{
                                                                options: {
                                                                    altInputClass: 'form-control',
                                                                    altFormat: DATE_ONLY_FORMAT,
                                                                    enableTime: false,
                                                                    dateFormat: DATE_ONLY_FORMAT,
                                                                    defaultDate: '',
                                                                },
                                                                value: values.dateTo
                                                                    ? moment(values.dateTo).format(
                                                                          DATE_ONLY_FORMAT,
                                                                      )
                                                                    : undefined,
                                                            }}
                                                            timezone={SERVER_TIMEZONE}
                                                            touched={touched.dateTo}
                                                            errors={errors.dateTo}
                                                            contentCssClass="col-sm-6"
                                                            placeholder="to"
                                                            testId="dateTo"
                                                        />
                                                    </div>

                                                    <ErrorMessage
                                                        name="dateFrom"
                                                        className="text-danger"
                                                        component="small"
                                                    />
                                                    <ErrorMessage
                                                        name="dateTo"
                                                        className="text-danger"
                                                        component="small"
                                                    />
                                                </div>
                                            </div>
                                        </Col>
                                        <Col>
                                            <AsyncMultiSelectField
                                                label="Template"
                                                name="templateCodes"
                                                touched={touched.templateCodes}
                                                errors={errors.templateCodes}
                                                asyncSelectProps={{
                                                    cacheOptions: true,
                                                    loadOptions: async (inputValue: string) => {
                                                        if (inputValue.length > 2) {
                                                            return templates
                                                                .filter((template) =>
                                                                    `${template.code} (${template.description})`
                                                                        .toUpperCase()
                                                                        .includes(
                                                                            inputValue.toUpperCase(),
                                                                        ),
                                                                )
                                                                .map((template) => ({
                                                                    value: template.code,
                                                                    label: `${template.code} (${template.description})`,
                                                                }));
                                                        }
                                                        return [];
                                                    },
                                                    value: values.templateCodes
                                                        ? values.templateCodes.map((code) => ({
                                                              label: code,
                                                              value: code,
                                                          }))
                                                        : [],
                                                    noOptionsMessage: noOptionsHandler,
                                                }}
                                                tooltip="To get a list of templates, <br/>try typing one of these prefixes:<br/>NOA, NOCL, NST, NTD, RETRO"
                                                labelCssClass="col-sm-4"
                                                contentCssClass="col-sm-8"
                                            />
                                        </Col>
                                        <Col>
                                            <MultiSelectField
                                                selectProps={
                                                    {
                                                        closeMenuOnSelect: false,
                                                    } as any
                                                }
                                                selectedOptions={
                                                    values.amStatuses
                                                        ? statusOptions
                                                              .filter((statusOption) =>
                                                                  values.amStatuses.includes(
                                                                      statusOption.value,
                                                                  ),
                                                              )
                                                              .map((statusOption) => ({
                                                                  label: statusOption.label,
                                                                  value: statusOption.value,
                                                              }))
                                                        : []
                                                }
                                                options={statusOptions}
                                                label="Status"
                                                name="amStatuses"
                                                touched={touched.amStatuses}
                                                errors={errors.amStatuses}
                                                labelCssClass="col-sm-4"
                                                contentCssClass="col-sm-8"
                                                testId="amStatuses"
                                            />
                                        </Col>
                                    </Row>
                                </Col>
                                <Col
                                    xs="1"
                                    className="align-self-center align-self-middle text-nowrap mb-3"
                                >
                                    <SubmitButton
                                        className="btn-primary"
                                        disabled={searching}
                                        isSubmitting={isSubmitting}
                                        buttonText={<i className="fas fa-search-plus" />}
                                    />
                                    <Button
                                        variant="outline-primary"
                                        className="ml-1"
                                        onClick={() => {
                                            setValues({
                                                personId: '',
                                                personName: '',
                                                amStatuses: [],
                                                dateFrom: undefined,
                                                dateTo: undefined,
                                                templateCodes: [],
                                                projectRoleIds: [],
                                            });
                                        }}
                                    >
                                        <i className="fas fa-backspace" />
                                    </Button>
                                </Col>
                            </Row>
                        </Container>
                    </Form>
                )}
            />
        </Container>
    );
};

export default UserManagementFilter;
